/**
 * GitLab admin API client — Phase 13 Plan 13-03 Task 1.
 *
 * Thin fetch wrappers around Plan 13-02's `/api/gitlab/admin/*` super-admin
 * endpoints. Auth rides the same `Authorization: Bearer <jwt>` header every
 * other admin surface uses (apiRequest from @/lib/api-client) — the
 * SuperAdminUser extractor on the backend reads the bearer, not the cookie.
 *
 * The 503 + GITLAB_DISABLED contract from Plan 13-02 is mapped to a typed
 * `GitLabDisabledError` so UI code can `error instanceof GitLabDisabledError`
 * and render a "GitLab integration not configured" empty state instead of a
 * generic toast.
 *
 * Note: Plan 13-01's Rust DTOs already serialize via `rename_all = "camelCase"`
 * so we read the wire shape directly via Zod parse — NO mapIssue / mapUser /
 * mapProject helpers. The old admin's mapping layer was a workaround for the
 * NestJS backend's mixed casing and is intentionally dropped here.
 */

import { z } from 'zod'

import { apiRequest } from '@/lib/api-client'

import {
  IssueSchema,
  MilestoneSchema,
  ProjectSchema,
  UserSchema,
  WorkspaceSchema,
  type CreateIssueInput,
  type CreateWorkspaceInput,
  type Issue,
  type Milestone,
  type Project,
  type UpdateIssueInput,
  type UpdateWorkspaceInput,
  type User,
  type Workspace,
} from './types'

// ─── Error class for the disabled-state contract ────────────────────────────

/**
 * Thrown when the sidecar responds with `503` + `code: 'GITLAB_DISABLED'`.
 * UI code catches via `error instanceof GitLabDisabledError` and renders a
 * "configure GITLAB_PAT" banner — see KanbanBoard's GitLabDisabledBanner.
 */
export class GitLabDisabledError extends Error {
  public readonly code = 'GITLAB_DISABLED' as const
  constructor(message: string) {
    super(message)
    this.name = 'GitLabDisabledError'
    // Restore prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, GitLabDisabledError.prototype)
  }
}

// ─── Internal helpers ────────────────────────────────────────────────────────

const BASE = '/api/gitlab/admin'

/** Locked envelope for list endpoints — mirrors Plan 13-02 ListResponse<T>. */
const ListEnvelopeSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), total: z.number() })

async function fetchJson<T>(
  path: string,
  init?: RequestInit,
  schema?: z.ZodSchema<T>,
): Promise<T> {
  // apiRequest auto-attaches Authorization: Bearer <jwt> from localStorage.
  // SuperAdminUser extractor on the backend reads the bearer header, not
  // cookies — the previous `credentials: 'include'` was a no-op for auth.
  const response = await apiRequest(`${BASE}${path}`, init)

  if (!response.ok) {
    // Try to parse a structured error envelope from the backend. The
    // Plan 13-02 contract is { error, code, details } — `code` is the
    // stable string we pattern-match against (never the human message).
    const data: { error?: string; code?: string; details?: unknown } =
      await response.json().catch(() => ({}))

    if (response.status === 503 && data.code === 'GITLAB_DISABLED') {
      throw new GitLabDisabledError(
        data.error || 'GitLab integration disabled — GITLAB_PAT not configured',
      )
    }
    throw new Error(data.error || `Request failed (${response.status})`)
  }

  // Some endpoints (e.g. DELETE workspace) return 204 with no body.
  if (response.status === 204) return undefined as T

  const json = await response.json()
  return schema ? schema.parse(json) : (json as T)
}

async function fetchList<T>(
  path: string,
  itemSchema: z.ZodSchema<T>,
): Promise<T[]> {
  const envelope = await fetchJson(
    path,
    undefined,
    ListEnvelopeSchema(itemSchema),
  )
  return envelope.items
}

// ─── Public API surface ──────────────────────────────────────────────────────

export const gitlabApi = {
  // GitLab REST proxy (503-gated when GITLAB_PAT is missing) ────────────────

  getProjects: (): Promise<Project[]> => fetchList('/projects', ProjectSchema),

  getMilestones: (projectId: number): Promise<Milestone[]> =>
    fetchList(`/projects/${projectId}/milestones`, MilestoneSchema),

  getMembers: (projectId: number): Promise<User[]> =>
    fetchList(`/projects/${projectId}/members`, UserSchema),

  getIssues: (
    projectIds: number[],
    state: 'opened' | 'closed' | 'all' = 'all',
  ): Promise<Issue[]> => {
    if (projectIds.length === 0) return Promise.resolve([])
    const params = new URLSearchParams({
      projectIds: projectIds.join(','),
      state,
    })
    return fetchList(`/issues?${params.toString()}`, IssueSchema)
  },

  getIssue: (projectId: number, iid: number): Promise<Issue> =>
    fetchJson(`/issues/${projectId}/${iid}`, undefined, IssueSchema),

  createIssue: (input: CreateIssueInput): Promise<Issue> =>
    fetchJson(
      '/issues',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      IssueSchema,
    ),

  updateIssue: (
    projectId: number,
    iid: number,
    input: UpdateIssueInput,
  ): Promise<Issue> =>
    fetchJson(
      `/issues/${projectId}/${iid}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      IssueSchema,
    ),

  // Workspace CRUD (Postgres-only — NOT 503-gated per Plan 13-02) ───────────

  listWorkspaces: (): Promise<Workspace[]> =>
    fetchList('/workspaces', WorkspaceSchema),

  createWorkspace: (input: CreateWorkspaceInput): Promise<Workspace> =>
    fetchJson(
      '/workspaces',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      WorkspaceSchema,
    ),

  updateWorkspace: (
    id: string,
    input: UpdateWorkspaceInput,
  ): Promise<Workspace> =>
    fetchJson(
      `/workspaces/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      WorkspaceSchema,
    ),

  deleteWorkspace: (id: string): Promise<void> =>
    fetchJson<void>(`/workspaces/${id}`, { method: 'DELETE' }),
} as const

export type GitlabApi = typeof gitlabApi
